import { Alert, Box } from '@mui/material';
import { RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement, type SyntheticEvent } from 'react';
import { useSearchParams } from 'react-router';

import { createAdminApi } from '@/admin/adminApi';
import { useHttpClient } from '@/app/providers';
import { Badge, Button, Card, DataTable, EmptyState, Field, LoadingState, PageHeader, Tabs } from '@/ui/kit';

import { BatchLineage } from './BatchLineage';
import { BatchSummary } from './BatchSummary';
import { IntegrityChecks } from './IntegrityChecks';
import { MovementsTable } from './MovementsTable';
import { ProblemView } from './ProblemView';
import { STOCK_CODE_PARAM } from './routes';
import { ScopeIntegrity } from './ScopeIntegrity';
import { batchCodes, describeStatus, describeStockProblem, shortId, summarizeIntegrity } from './stockPresentation';
import type { StockLookupItem, StockTrace } from './stockTypes';
import { useServerRead } from './useServerRead';

/**
 * Stock et tracabilite (BO-19, #516) : retrouver un lot par son code, ou par
 * l'identifiant d'un lot, d'un tirage ou d'un biberon, puis lire sa fiche, sa
 * genealogie, ses mouvements et les controles d'integrite.
 *
 * Cinq regles gouvernent cet ecran :
 *
 * 1. **Lecture seule.** Aucune operation de stock n'est exposee a l'administration
 *    (ADR 0011) : aucun bouton n'ecrit, et l'ecran le dit.
 * 2. **L'invite initiale n'appelle aucune route.** Chaque lecture d'une route
 *    d'administration ecrit une ligne d'audit (ADR 0035 §4) : la recherche est un
 *    geste explicite (Entree ou « Rechercher »), jamais une frappe. Le lien
 *    profond `/stock?code=` est ce geste, fait par quelqu'un d'autre.
 * 3. **Aucun rafraichissement automatique.** « Actualiser », et une relecture au
 *    retour sur l'onglet, suffisent a l'exploitation.
 * 4. **Rien que ce que le serveur rend.** Les compteurs des onglets sont le
 *    nombre reel de mouvements et d'incoherences de la reponse ; une valeur
 *    illisible s'affiche « — ».
 * 5. **Un refus de role n'est pas une panne.** Un 403 explique ce qui manque et ne
 *    deconnecte personne ; un lot sans trace, un compte anonymise ou un journal
 *    trop volumineux sont des constats, pas des erreurs a reessayer.
 *
 * La vue est celle du **serveur** : les saisies que l'appareil n'a pas encore
 * synchronisees n'y figurent pas.
 */

type TabValue = 'lot' | 'movements' | 'integrity';

const TAB_LABELS: Readonly<Record<TabValue, string>> = {
  lot: 'Lot',
  movements: 'Mouvements',
  integrity: 'Contrôles d’intégrité',
};

const PANEL_ID = 'stock-trace-panel';

/** Ce qu'on lit d'une trace, avec l'instant de la lecture (c'est lui qui date « expiré »). */
interface LoadedTrace {
  readonly trace: StockTrace;
  readonly readAt: Date;
}

/** Plusieurs lots portent ou citent la valeur saisie : on choisit, on n'en ouvre pas un au hasard. */
const LotChoice = ({
  items,
  onChoose,
}: {
  items: readonly StockLookupItem[];
  onChoose: (item: StockLookupItem) => void;
}): ReactElement => (
  <Card
    title="Plusieurs lots correspondent"
    subtitle={`${String(items.length)} lots portent ou citent cette valeur. Choisissez celui à ouvrir.`}
  >
    <DataTable<StockLookupItem>
      label="Lots correspondant à la recherche"
      rows={items}
      getRowId={(item) => `${item.babyId}/${item.batchId}`}
      columns={[
        { key: 'code', header: 'Code', mono: true, primary: true, render: (item) => item.code ?? `Lot ${shortId(item.batchId)}` },
        {
          key: 'status',
          header: 'Statut',
          render: (item) => {
            const status = describeStatus(item.status);

            return status === null ? null : <Badge label={status.label} tone={status.tone} />;
          },
        },
        { key: 'scope', header: 'Périmètre', mono: true, render: (item) => shortId(item.babyId) },
        {
          key: 'open',
          header: 'Action',
          render: (item) => (
            <Button
              size="S"
              variant="secondary"
              aria-label={`Ouvrir le lot ${item.code ?? shortId(item.batchId)}`}
              onClick={() => {
                onChoose(item);
              }}
            >
              Ouvrir
            </Button>
          ),
        },
      ]}
    />
  </Card>
);

const TracePanels = ({
  loaded,
  scopeId,
  tab,
  onTab,
}: {
  loaded: LoadedTrace;
  /** Périmètre du journal du lot : celui que la recherche a rendu. */
  scopeId: string;
  tab: TabValue;
  onTab: (tab: TabValue) => void;
}): ReactElement => {
  const { trace, readAt } = loaded;
  const codes = batchCodes(trace.batch, trace.lineage);

  return (
    <>
      <Tabs<TabValue>
        label="Sections de la traçabilité du lot"
        value={tab}
        onChange={onTab}
        panelId={PANEL_ID}
        items={[
          { value: 'lot', label: TAB_LABELS.lot },
          { value: 'movements', label: TAB_LABELS.movements, count: trace.movements.length },
          { value: 'integrity', label: TAB_LABELS.integrity, count: summarizeIntegrity(trace.integrity).inconsistencies },
        ]}
      />
      <Box role="tabpanel" id={PANEL_ID} aria-label={TAB_LABELS[tab]} sx={{ display: 'grid', gap: 2 }}>
        {tab === 'lot' ? (
          <>
            <BatchSummary batch={trace.batch} scopeId={scopeId} now={readAt} />
            <BatchLineage batch={trace.batch} lineage={trace.lineage} scopeId={scopeId} />
          </>
        ) : null}
        {tab === 'movements' ? (
          <MovementsTable batch={trace.batch} movements={trace.movements} lineage={trace.lineage} />
        ) : null}
        {tab === 'integrity' ? (
          <>
            <IntegrityChecks
              title="Contrôles d’intégrité"
              icon={ShieldCheck}
              subtitle="Rejoués à la demande sur ce lot, depuis ce que le journal affirme. Rien n’y est corrigé."
              listLabel="Contrôles d’intégrité du lot"
              checks={trace.integrity}
              currentBatchId={trace.batch.id}
              codes={codes}
            />
            <ScopeIntegrity key={scopeId} scopeId={scopeId} currentBatchId={trace.batch.id} codes={codes} />
          </>
        ) : null}
      </Box>
    </>
  );
};

/** Relit au retour sur l'onglet du navigateur, jamais sur minuterie (ADR 0035 §4). */
const useReloadOnReturn = (enabled: boolean, reload: () => void): void => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onChange = (): void => {
      if (document.visibilityState === 'visible') {
        reload();
      }
    };

    document.addEventListener('visibilitychange', onChange);

    return () => {
      document.removeEventListener('visibilitychange', onChange);
    };
  }, [enabled, reload]);
};

export const StockTraceScreen = (): ReactElement => {
  const http = useHttpClient();
  const api = useMemo(() => createAdminApi(http), [http]);
  const [searchParams, setSearchParams] = useSearchParams();
  const code = (searchParams.get(STOCK_CODE_PARAM) ?? '').trim();

  // Le champ suit l'adresse : un lien profond (palette, fiche de compte) ou un clic
  // dans la genealogie change `?code=` sans passer par lui. Ajuster l'etat pendant
  // le rendu est le patron que React recommande a la place d'un effet.
  const [draft, setDraft] = useState(code);
  const [syncedCode, setSyncedCode] = useState(code);

  if (syncedCode !== code) {
    setSyncedCode(code);
    setDraft(code);
  }

  const readLookup = useMemo(() => (code === '' ? null : () => api.lookupStockBatches(code)), [api, code]);
  const lookup = useServerRead(readLookup);
  const items = lookup.state.phase === 'idle' ? null : lookup.state.data;

  // Choix parmi plusieurs lots, rattache a la recherche qui l'a propose : une autre
  // recherche l'oublie.
  const [chosen, setChosen] = useState<{ readonly forCode: string; readonly item: StockLookupItem } | null>(null);
  const selected = items?.length === 1 ? (items[0] ?? null) : chosen?.forCode === code ? chosen.item : null;
  const scopeId = selected?.babyId ?? null;
  const batchId = selected?.batchId ?? null;

  const readTrace = useMemo(
    () =>
      scopeId === null || batchId === null
        ? null
        : async (): Promise<LoadedTrace> => ({ trace: await api.getStockTrace(scopeId, batchId), readAt: new Date() }),
    [api, scopeId, batchId],
  );
  const trace = useServerRead(readTrace);
  const loaded = trace.state.phase === 'idle' ? null : trace.state.data;

  // L'onglet ouvert appartient au lot : un autre lot s'ouvre sur « Lot ».
  const [tabState, setTabState] = useState<{ readonly batchId: string | null; readonly tab: TabValue }>({
    batchId: null,
    tab: 'lot',
  });
  const tab = tabState.batchId === batchId ? tabState.tab : 'lot';

  useReloadOnReturn(loaded !== null, trace.reload);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();

    const next = draft.trim();

    if (next === '') {
      return;
    }

    if (next === code) {
      // Meme valeur : l'adresse ne change pas, mais le geste demande une relecture
      // — et, s'il y avait un choix a faire, le rend.
      setChosen(null);
      lookup.reload();

      return;
    }

    setSearchParams({ [STOCK_CODE_PARAM]: next });
  };

  const failure = (state: typeof lookup.state | typeof trace.state, retry: () => void): ReactElement | null =>
    state.phase === 'failed' ? <ProblemView problem={describeStockProblem(state.error)} onRetry={retry} /> : null;

  return (
    <Box sx={{ display: 'grid', gap: 3, mt: 0.5, mb: 4 }}>
      <PageHeader
        eyebrow="Données"
        title="Traçabilité d’un lot"
        subtitle="D’où vient ce lot, où est-il passé, que reste-t-il. Rien ne se modifie ici, tout se lit."
        actions={
          <Box
            component="form"
            role="search"
            onSubmit={submit}
            sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
          >
            <Box sx={{ width: { xs: '100%', sm: 380 } }}>
              <Field
                label="Code de lot, tirage ou biberon"
                value={draft}
                onChange={setDraft}
                icon={Search}
                placeholder="MM-20261012T071500-01-a1b2c3 ou identifiant"
                autoComplete="off"
              />
            </Box>
            <Button type="submit" disabled={draft.trim() === ''}>
              Rechercher
            </Button>
            {loaded === null ? null : (
              <Button
                variant="secondary"
                icon={RefreshCw}
                disabled={trace.state.phase === 'loading'}
                onClick={trace.reload}
              >
                Actualiser
              </Button>
            )}
          </Box>
        }
      />

      <Alert severity="info" variant="outlined" role="note">
        Vue du serveur : les saisies pas encore synchronisées ne figurent pas ici.
      </Alert>

      {code === '' ? (
        <EmptyState
          icon={Search}
          title="Entrez un code pour commencer"
          description="Saisissez le code d’un lot, ou l’identifiant d’un lot, d’une séance de tirage ou d’un biberon. Rien n’est lu tant que vous n’avez pas lancé la recherche."
        />
      ) : null}

      {lookup.state.phase === 'loading' && items === null ? <LoadingState label="Recherche du lot…" /> : null}
      {failure(lookup.state, lookup.reload)}

      {lookup.state.phase === 'ready' && items !== null && items.length === 0 ? (
        <EmptyState
          title="Aucun lot ne porte ce code."
          description="Vérifiez le code, ou saisissez l’identifiant d’un lot, d’une séance de tirage ou d’un biberon. Une saisie pas encore synchronisée n’est pas visible ici."
        />
      ) : null}

      {items !== null && items.length > 1 && selected === null ? (
        <LotChoice
          items={items}
          onChoose={(item) => {
            setChosen({ forCode: code, item });
          }}
        />
      ) : null}

      {selected !== null && trace.state.phase === 'loading' && loaded === null ? (
        <LoadingState label="Reconstitution du lot depuis le journal…" />
      ) : null}
      {failure(trace.state, trace.reload)}

      {loaded === null || scopeId === null ? null : (
        <TracePanels
          loaded={loaded}
          scopeId={scopeId}
          tab={tab}
          onTab={(next) => {
            setTabState({ batchId, tab: next });
          }}
        />
      )}
    </Box>
  );
};
